import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, HostListener, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';

interface ExpenseRow { id: string; code: string; bookingCode: string; customerName: string; tripName: string; travelDate: string; travelerCount: number; bookingTotal: number; title: string; expenseType: string; requester: string; amount: number; purchaseOrderFileName: string; status: string; createdAt: string; }
const EXPENSES: ExpenseRow[] = [
  { id: '1', code: 'EXP-2026-000001', bookingCode: 'BK-2026-000001', customerName: 'บริษัท สยามท่องเที่ยว จำกัด', tripName: 'ญี่ปุ่น โตเกียว ฟูจิ 6 วัน', travelDate: '2026-09-10', travelerCount: 4, bookingTotal: 100000, title: 'ค่าที่พักกรุ๊ปญี่ปุ่น', expenseType: 'ค่าที่พัก', requester: 'สมชาย ใจดี', amount: 32000, purchaseOrderFileName: 'PO-JP-0001.pdf', status: 'SUBMITTED', createdAt: '2026-08-07' },
  { id: '2', code: 'EXP-2026-000002', bookingCode: 'BK-2026-000002', customerName: 'คุณพิมพ์ชนก แสนดี', tripName: 'เกาหลี โซล 5 วัน', travelDate: '2026-10-02', travelerCount: 2, bookingTotal: 68500, title: 'ค่ารถรับส่งสนามบิน', expenseType: 'ค่าเดินทาง', requester: 'วราภรณ์ ดีมาก', amount: 12500, purchaseOrderFileName: 'PO-KR-0002.pdf', status: 'SUPERVISOR_REVIEW', createdAt: '2026-08-09' },
];

@Component({
  selector: 'app-expenses-page',
  imports: [DatePipe, DecimalPipe, ReactiveFormsModule, LucideAngularModule, StatusBadge, TableModule, InputNumberModule, InputTextModule, SelectModule, FileUploadModule],
  templateUrl: './expenses-page.component.html',
})
export class ExpensesPageComponent {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly fb = inject(FormBuilder);
  readonly expenses = signal(EXPENSES);
  readonly isExpenseModalOpen = signal(false);
  readonly selectedExpense = signal<ExpenseRow | null>(null);
  readonly purchaseOrderFileName = signal('');
  readonly expenseTypeOptions = ['ค่าที่พัก', 'ค่าเดินทาง', 'ค่าอาหาร', 'ค่าบัตรเข้าชม', 'ค่าใช้จ่ายอื่น ๆ'];
  readonly expenseForm = this.fb.group({
    bookingCode: ['', Validators.required],
    title: ['', Validators.required],
    expenseType: ['', Validators.required],
    amount: [null as number | null, [Validators.required, Validators.min(1)]],
  });

  openExpenseModal(): void {
    this.expenseForm.reset();
    this.purchaseOrderFileName.set('');
    this.isExpenseModalOpen.set(true);
  }
  closeExpenseModal(): void { this.isExpenseModalOpen.set(false); }
  openExpenseDetail(row: ExpenseRow): void { this.selectedExpense.set(row); }
  closeExpenseDetail(): void { this.selectedExpense.set(null); }
  selectPurchaseOrder(event: FileSelectEvent): void {
    this.purchaseOrderFileName.set(event.files[0]?.name ?? '');
  }
  isInvalid(field: keyof typeof this.expenseForm.controls): boolean {
    const control = this.expenseForm.controls[field];
    return control.touched && control.invalid;
  }
  saveExpense(): void {
    if (this.expenseForm.invalid || !this.purchaseOrderFileName()) {
      this.expenseForm.markAllAsTouched();
      return;
    }
    const value = this.expenseForm.getRawValue();
    const now = new Date();
    this.expenses.update((rows) => [{
      id: String(Date.now()),
      code: `EXP-${now.getFullYear()}-${String(rows.length + 1).padStart(6, '0')}`,
      bookingCode: value.bookingCode ?? '-', customerName: '-', tripName: '-', travelDate: now.toISOString().slice(0, 10), travelerCount: 0, bookingTotal: 0,
      title: value.title ?? '-', expenseType: value.expenseType ?? '-', purchaseOrderFileName: this.purchaseOrderFileName(),
      requester: 'ผู้ใช้งานปัจจุบัน',
      amount: value.amount ?? 0,
      status: 'SUBMITTED',
      createdAt: now.toISOString().slice(0, 10),
    }, ...rows]);
    this.closeExpenseModal();
    this.overlay.success('สร้างใบขอเบิกสำเร็จ', `แนบไฟล์ ${this.purchaseOrderFileName()} เรียบร้อยแล้ว`);
  }
  filterExpenses(term: string): void {
    const query = term.trim().toLowerCase();
    this.expenses.set(query ? EXPENSES.filter((row) => `${row.code} ${row.title} ${row.requester}`.toLowerCase().includes(query)) : EXPENSES);
  }
  async approveExpense(row: ExpenseRow): Promise<void> {
    const confirmed = await this.overlay.confirm({ title: 'อนุมัติใบขอเบิก', message: `ยืนยันอนุมัติ ${row.code} จำนวน ${row.amount.toLocaleString()} บาท หรือไม่`, confirmText: 'อนุมัติ' });
    if (confirmed) this.overlay.success('อนุมัติสำเร็จ');
  }
  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.isExpenseModalOpen()) this.closeExpenseModal(); if (this.selectedExpense()) this.closeExpenseDetail(); }
}
